import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { searchMailLogs, getMailTrace } from "../../services/MailLogs";
import DatePicker, { registerLocale } from "react-datepicker";
import { pl } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import CardHeader from "../../components/common/CardHeader";
import TraceWindow from "../../components/common/TraceWindow";

registerLocale("pl", pl);

const dsnHints = {
  2: "Sukces – serwer odbiorcy potwierdził przyjęcie wiadomości.",
  4: "Tymczasowy problem po stronie odbiorcy (spróbujemy ponownie).",
  5: "Błąd trwały – wiadomość nie została doręczona.",
  "5.1.1": "Adres odbiorcy nie istnieje.",
  "5.2.0": "Skrzynka pełna lub ograniczona.",
  "5.7.1": "Odrzucono z powodów polityki bezpieczeństwa/autoryzacji.",
  "4.4.1": "Problem z połączeniem/timeout do serwera odbiorcy.",
};

function interpretResult(status, dsn) {
  const s = (status || "").toLowerCase();
  const d = dsn || "";
  if (s.startsWith("sent") || d.startsWith("2.")) {
    return {
      label: "Dostarczono",
      variant: "success",
      iconClass: "bi-check-circle-fill",
      hint: dsnHints["2"],
    };
  }
  if (s.startsWith("deferred") || d.startsWith("4.")) {
    const hint = dsnHints[d] || dsnHints["4"];
    return {
      label: "Opóźnione",
      variant: "warning",
      iconClass: "bi-exclamation-triangle-fill",
      hint,
    };
  }
  if (s.startsWith("bounced") || s.startsWith("reject") || d.startsWith("5.")) {
    const hint = dsnHints[d] || dsnHints["5"];
    return {
      label: "Odrzucono",
      variant: "danger",
      iconClass: "bi-x-circle-fill",
      hint,
    };
  }
  return {
    label: "Nieznany",
    variant: "secondary",
    iconClass: "bi-info-circle-fill",
    hint: "Brak jednoznacznej informacji w logu.",
  };
}

function toYmd(date) {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatTs(ts) {
  try {
    const d = new Date(ts);
    return `${d.toLocaleDateString("pl-PL")}\n${d.toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })}`;
  } catch {
    return ts || "";
  }
}

function Badge({ variant = "secondary", children }) {
  const cls =
    {
      success: "bg-success",
      warning: "bg-warning text-dark",
      danger: "bg-danger",
      secondary: "bg-secondary",
    }[variant] || "bg-secondary";
  return <span className={`badge ${cls}`}>{children}</span>;
}

export default function MailLogsPage() {
  const { accessToken } = useAuth();
  const [email, setEmail] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);

  const [sortCol, setSortCol] = useState("ts");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("mailLogs_pageSize");
    return saved ? parseInt(saved, 10) : 25;
  });
  useEffect(() => {
    localStorage.setItem("mailLogs_pageSize", String(pageSize));
  }, [pageSize]);

  const [traceWins, setTraceWins] = useState([]);
  const [zTop, setZTop] = useState(1000);

  function openTrace(queueId) {
    setTraceWins((prev) => {
      const exist = prev.find((w) => w.queueId === queueId);
      if (exist) {
        bringToFront(exist.id);
        return prev;
      }
      const id = (prev[prev.length - 1]?.id || 0) + 1;
      const z = zTop + 1;
      setZTop(z);
      return [...prev, { id, queueId, z }];
    });
  }

  function closeTrace(id) {
    setTraceWins((prev) => prev.filter((w) => w.id !== id));
  }

  function bringToFront(id) {
    setTraceWins((prev) => {
      const z = zTop + 1;
      setZTop(z);
      return prev.map((w) => (w.id === id ? { ...w, z } : w));
    });
  }

  useEffect(() => {
    doSearch();
  }, []);

  const columns = useMemo(
    () => [
      { key: "ts", label: "Data wysyłki" },
      { key: "to", label: "Do" },
      //{ key: "from", label: "Od" },
      { key: "result", label: "Wynik" },
      { key: "dsn", label: "DSN" },
      { key: "relay", label: "Serwer odbiorcy" },
      //{ key: "message_id", label: "Message-ID" },
      { key: "queue_id", label: "Identyfikator wysyłki (Queue ID)" },
      { key: "actions", label: "Akcje" },
    ],
    []
  );

  function doSearch(e, overrides = {}) {
    e?.preventDefault();
    setError("");

    const fromDate = overrides.from ?? dateFrom;
    const toDate = overrides.to ?? dateTo;
    const emailRaw = overrides.email ?? email;

    if (fromDate && toDate && fromDate > toDate) {
      setError("Zakres dat jest nieprawidłowy (Od > Do).");
      return;
    }

    const fromY = toYmd(fromDate);
    const toY = toYmd(toDate);
    const emailParam = (emailRaw || "").trim().toLowerCase() || undefined;

    setLoading(true);
    searchMailLogs(emailParam, fromY, toY, 500, accessToken)
      .then((itemsRaw) => {
        const items = (itemsRaw || []).map((x) => {
          const info = interpretResult(x.status, x.dsn);
          return {
            ...x,
            _resultInfo: info,
            result: `${info.label}`,
          };
        });
        setRows(items);
        setPage(1);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Nie udało się pobrać wyników.");
      })
      .finally(() => setLoading(false));
  }

  function clearFilters() {
    setEmail("");
    setDateFrom(null);
    setDateTo(null);
    setError("");
    setLoading(true);
    searchMailLogs(null, null, null, 500, accessToken)
      .then((itemsRaw) => {
        const items = (itemsRaw || []).map((x) => {
          const info = interpretResult(x.status, x.dsn);
          return {
            ...x,
            _resultInfo: info,
            result: `${info.label}`,
          };
        });
        setRows(items);
        setPage(1);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Nie udało się pobrać wyników.");
      })
      .finally(() => setLoading(false));
  }

  function toggleSort(key) {
    if (sortCol === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(key);
      setSortDir("asc");
    }
  }

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const A = a[sortCol];
      const B = b[sortCol];
      if (sortCol === "ts") {
        const ad = A ? new Date(A).getTime() : 0;
        const bd = B ? new Date(B).getTime() : 0;
        return sortDir === "asc" ? ad - bd : bd - ad;
      }
      if (A === B) return 0;
      return sortDir === "asc" ? (A > B ? 1 : -1) : A < B ? 1 : -1;
    });
    return arr;
  }, [rows, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageItems = sorted.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <CardHeader
        text={"Dziennik wysyłek e‑mail"}
        onlyText="true"
        commentId={null}
        commentLocation={null}
        commentVisible={null}
        registerCallback={null}
        viewAll={null}
        hint={
          "Wpisz opcjonalne filtry takie jak adres email odbiorcy i zakres dat, aby zobaczyć próby doręczenia."
        }
      />

      <div className="card container-fluid shadow ms-4 px-4 py-3 w80">
        <form className="border rounded p-3 mb-3" onSubmit={doSearch}>
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label">Adres e‑mail odbiorcy</label>
              <input
                type="email"
                className="form-control"
                placeholder="np. jan.kowalski@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <label className="form-label mt-2">Zakres dat</label>
            <div className="col-md-6 d-flex gap-2">
              <DatePicker
                selected={dateFrom}
                onChange={setDateFrom}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                placeholderText="Od"
                locale="pl"
                dateFormat="yyyy-MM-dd"
                className="form-control"
              />
              <div className="form-control rounded-0 border-start-0 border-end-0 w3 px-1 h2 d-flex justify-content-center align-items-center">
                <i className="bi bi-arrow-right"></i>
              </div>

              <DatePicker
                selected={dateTo}
                onChange={setDateTo}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                placeholderText="Do"
                locale="pl"
                dateFormat="yyyy-MM-dd"
                className="form-control"
              />
            </div>
          </div>
          <div className="mt-2 d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={clearFilters}
            >
              <i class="bi bi-x"></i> Wyczyść
            </button>
            <div className="col-md-1 d-grid">
              <button
                className="btn btn-primary d-flex gap-2"
                style={{ justifyContent: "center", alignItems: "center" }}
                type="submit"
                disabled={loading}
              >
                {loading && (
                  <span
                    className="spinner-border spinner-border-sm "
                    role="status"
                  />
                )}{" "}
                Szukaj
              </button>
            </div>
          </div>
          {error && <div className="alert alert-danger mt-2 mb-0">{error}</div>}
        </form>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    role="button"
                    onClick={() => toggleSort(c.key)}
                  >
                    {c.label}
                    {sortCol === c.key && (
                      <i
                        className={`ms-1 bi ${
                          sortDir === "asc"
                            ? "bi-caret-up-fill"
                            : "bi-caret-down-fill"
                        }`}
                      ></i>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!pageItems.length && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center text-muted py-4"
                  >
                    Brak danych – wpisz adres i kliknij „Szukaj”.
                  </td>
                </tr>
              )}
              {pageItems.map((row, idx) => {
                const info =
                  row._resultInfo || interpretResult(row.status, row.dsn);
                return (
                  <tr key={`${row.queue_id}-${idx}`}>
                    <td style={{ whiteSpace: "pre-line" }}>
                      {formatTs(row.ts)}
                    </td>
                    <td className="break-words">{row.to}</td>
                    {/* <td className="text-break">{row.from || "—"}</td> */}
                    <td>
                      <Badge variant={info.variant}>
                        <i className={`bi ${info.iconClass} me-1`}></i>
                        {info.label}
                      </Badge>
                      {row.dsn && (
                        <div className="small text-muted">
                          {row.dsn} – {info.hint}
                        </div>
                      )}
                    </td>
                    <td>{row.dsn || "—"}</td>
                    <td className="">
                      <span>{row.relay_host || row.relay || "—"}</span>
                      <br />
                      {row.relay_ip && (
                        <>
                          {" "}
                          <span className="text-muted">({row.relay_ip})</span>
                        </>
                      )}
                      {row.relay_port && (
                        <>
                          :<span className="text-muted">{row.relay_port}</span>
                        </>
                      )}
                    </td>
                    {/* <td className="text-break">{row.message_id || "—"}</td> */}
                    <td className="text-nowrap">
                      <div className="d-inline-flex align-items-center">
                        <code className="font-monospace fw-semibold">
                          {row.queue_id}
                        </code>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary p-1 lh-1"
                          title="Kopiuj Queue ID"
                          onClick={() =>
                            navigator.clipboard.writeText(row.queue_id)
                          }
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </td>

                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 text-nowrap"
                        onClick={() => openTrace(row.queue_id)}
                        title="Podgląd śladu"
                      >
                        <i className="bi bi-eye"></i>
                        <span>Podgląd śladu</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!!sorted.length && (
          <div className="d-flex align-items-center">
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    &laquo;
                  </button>
                </li>
                {Array.from({ length: totalPages })
                  .slice(0, 10)
                  .map((_, i) => {
                    const n = i + 1;
                    return (
                      <li
                        key={n}
                        className={`page-item ${page === n ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      </li>
                    );
                  })}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
            <div className="d-flex align-items-center gap-2 ms-1">
              <label className="text-muted mb-0">Rozmiar strony:</label>
              <select
                className="form-select form-select-sm w-auto"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span className="ms-auto text-muted">
              Wyniki: {(page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, sorted.length)} z {sorted.length}
            </span>
          </div>
        )}

        {traceWins.map((w) => (
          <TraceWindow
            key={w.id}
            id={w.id}
            queueId={w.queueId}
            zIndex={w.z}
            onClose={closeTrace}
            bringToFront={bringToFront}
          />
        ))}
      </div>
    </>
  );
}
